import React from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, UserX, Users, Zap, Shield, Lock, FileCheck, Key } from 'lucide-react';
import SEO from '@/components/SEO';

const Services = () => {
  const useCases = [
    {
      icon: Users,
      title: 'Identity Lifecycle',
      desc: 'Automate user lifecycle from day one to day last. Timely access, secure offboarding.',
      features: ['HRIS-triggered automated provisioning', 'Role-based access templates', 'Immediate deprovisioning on departure', 'Complete audit trail']
    },
    {
      icon: Key,
      title: 'Access Management',
      desc: 'Seamless SSO integration, passwordless authentication, MFA enforcement, and federation support for SAML 2.0, OpenID Connect, and FIDO2/WebAuthn.',
      features: ['Single Sign-On (SSO) across applications', 'Passwordless authentication', 'Multi-Factor Authentication (MFA)', 'SAML 2.0, OpenID Connect, FIDO2/WebAuthn']
    },
    {
      icon: Zap,
      title: 'Improve IT Efficiency',
      desc: 'Streamline operations, reduce manual work. Free up IT resources with automation.',
      features: ['Event-driven automation', 'Self-service access requests', 'Reduce helpdesk tickets', 'Centralized identity management']
    },
    {
      icon: Shield,
      title: 'Mitigate Cyber Risk',
      desc: 'Protect from identity-based attacks. Comprehensive security controls for your perimeter.',
      features: ['360-degree access visibility', 'Detect toxic access combinations', 'Real-time threat detection', 'Automated risk scoring']
    },
    {
      icon: Lock,
      title: 'Embrace Zero Trust',
      desc: 'Verify every identity and access request. Never trust, always verify.',
      features: ['Always verify approach', 'Least privilege access', 'Continuous monitoring', 'Identity-based perimeter']
    },
    {
      icon: FileCheck,
      title: 'Maintain Compliance',
      desc: 'Meet regulatory requirements. Automated reviews and audit-ready reporting.',
      features: ['Automated access certifications', 'Compliance gap analysis', 'Audit-ready reporting', 'Segregation of duties']
    },
  ];

  return (
    <div>
      <SEO
        title="Identity & Access Management Services"
        description="Comprehensive IAM services including identity lifecycle management, SSO, MFA, access control, and cybersecurity solutions for growing companies."
        keywords="IAM services, identity lifecycle, SSO implementation, MFA setup, access management, cybersecurity consulting"
        ogTitle="IDNTY Services - Identity & Access Management Solutions"
        ogDescription="Comprehensive IAM services including identity lifecycle management, SSO, MFA, and access control for growing companies."
        canonicalUrl="https://idnty.one/services"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Identity & Access Management Services',
          description: 'Comprehensive IAM services including identity lifecycle management, SSO, MFA, and access control.',
          provider: {
            '@type': 'Organization',
            name: 'IDNTY'
          },
          url: 'https://idnty.one/services'
        }}
      />
      {/* Hero */}
      <section className="section-padding" style={{ background: 'var(--white)' }}>
        <div className="container">
          <h1 className="hero-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            IAM IMPLEMENTATION + SECURITY POSTURE
          </h1>
          <p style={{ fontSize: '1.15rem', textAlign: 'center', maxWidth: '720px', margin: '0 auto 2rem', lineHeight: '1.8' }}>
            Most companies wait too long to implement proper identity infrastructure. They hit a forcing function — an enterprise deal that requires SSO, a SOC 2 audit, a security questionnaire they can't answer — and suddenly they're 6–12 months behind.
          </p>
          <div style={{ textAlign: 'center' }}>
            <Link to="/pricing" className="btn" style={{ marginRight: '0.75rem' }}>View Pricing</Link>
            <Link to="/contact" className="btn btn-secondary">Schedule Assessment</Link>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-padding" style={{ background: 'var(--light-gray)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '1rem' }}>
            COMPREHENSIVE IDENTITY AND ACCESS MANAGEMENT
          </h2>
          <p style={{ textAlign: 'center', maxWidth: '720px', margin: '0 auto 3rem', fontSize: '1.1rem', lineHeight: '1.7' }}>
            Use cases to secure your business and drive efficiency
          </p>

          <div className="grid-3" style={{ gap: '1.5rem' }}>
            {useCases.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="card-heavy" style={{ background: 'var(--white)', padding: '1.5rem' }}>
                  <Icon size={36} style={{ color: 'var(--gold)', marginBottom: '1rem' }} />
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--black)' }}>{item.title}</h3>
                  <p style={{ fontSize: '0.95rem', color: 'var(--gray)', marginBottom: '1rem', lineHeight: '1.6' }}>{item.desc}</p>
                  <ul style={{ paddingLeft: '1rem', margin: 0, fontSize: '0.9rem', lineHeight: '1.8', color: 'var(--gray)' }}>
                    {item.features.map((f, j) => (
                      <li key={j}>{f}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For / Not For */}
      <section className="section-padding" style={{ background: 'var(--charcoal)', color: 'var(--white)' }}>
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--white)' }}>
            WHO THIS IS FOR
          </h2>
          <div className="grid-2">
            <div style={{ border: '2px solid var(--gold)', padding: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <UserCheck size={28} style={{ color: 'var(--gold)' }} />
                <h3 className="feature-heading" style={{ color: 'var(--gold)', fontSize: '1.1rem', margin: 0 }}>You're a fit if</h3>
              </div>
              <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: '1.9', fontSize: '0.95rem', listStyleType: '"- "' }}>
                <li>Using multiple app logins and understand the importance of ensuring employees use MFA, passwords are not subject to phishing, and need it all managed centrally</li>
                <li>Evaluating or actively pursuing SOC 2</li>
                <li>Tired of security questionnaires revealing gaps</li>
                <li>Lost deals because prospects checked your security posture</li>
              </ul>
            </div>
            <div style={{ border: '2px solid var(--gray)', padding: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <UserX size={28} style={{ color: 'var(--gray)' }} />
                <h3 className="feature-heading" style={{ color: 'var(--gray)', fontSize: '1.1rem', margin: 0 }}>You're NOT a fit if</h3>
              </div>
              <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: '1.9', fontSize: '0.95rem', listStyleType: '"- "' }}>
                <li>Early stage team using a small number of apps? Talk to us — we'll help position your fundamentals correctly for growth and security.</li>
                <li>Need enterprise features like on-prem Active Directory or complex federation (talk to us — we may refer you to a larger consultancy)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" style={{ background: 'var(--black)', color: 'var(--white)' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <Link to="/contact" className="btn btn-gold">
            Schedule Assessment
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
